/**
 * Admin form for bulk-editing an order's regeneration counters.
 *
 * Renders three `<InputNumber>` fields (Cover / Book / Page) initialized from
 * the current order values. The Save button is enabled only when at least one
 * value differs from the server state; clicking it opens a `Modal.confirm`
 * with a diff summary and (on confirm) PATCHes ONLY the changed fields to
 * `/api/admin/orders/:id/regeneration-counts`.
 *
 * The server-side endpoint (see `apps/server/src/routes/admin/orders.ts`) emits
 * a Telegram audit notification identifying the admin (`req.adminUsername`) and
 * the per-field diff. Empty bodies / no-op diffs are rejected client-side
 * (Save disabled) and server-side (Zod refine).
 */
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { Card, Form, InputNumber, Button, App } from 'antd';
import {
  COVER_REGEN_LIMIT,
  BOOK_REGEN_LIMIT,
  PAGE_REGEN_LIMIT,
} from '@woolaby/types';

import { axiosInstance } from '@/providers/axiosInstance';

interface EditRegenerationCountsFormProps {
  orderId: string;
  coverRegenerationCount: number;
  bookRegenerationCount: number;
  pageRegenerationCount: number;
  onSuccess: () => void;
}

interface RegenerationCountsPatchBody {
  coverRegenerationCount?: number;
  bookRegenerationCount?: number;
  pageRegenerationCount?: number;
}

function helperText(current: number, limit: number): string {
  const remaining = Math.max(0, limit - current);
  return `${String(current)} / ${String(limit)} (${String(remaining)} remaining)`;
}

export function EditRegenerationCountsForm({
  orderId,
  coverRegenerationCount,
  bookRegenerationCount,
  pageRegenerationCount,
  onSuccess,
}: EditRegenerationCountsFormProps): ReactElement {
  const { message, modal } = App.useApp();

  const [coverValue, setCoverValue] = useState<number>(coverRegenerationCount);
  const [bookValue, setBookValue] = useState<number>(bookRegenerationCount);
  const [pageValue, setPageValue] = useState<number>(pageRegenerationCount);
  const [submitting, setSubmitting] = useState(false);

  const changedFields = useMemo((): RegenerationCountsPatchBody => {
    const body: RegenerationCountsPatchBody = {};
    if (coverValue !== coverRegenerationCount) {
      body.coverRegenerationCount = coverValue;
    }
    if (bookValue !== bookRegenerationCount) {
      body.bookRegenerationCount = bookValue;
    }
    if (pageValue !== pageRegenerationCount) {
      body.pageRegenerationCount = pageValue;
    }
    return body;
  }, [
    coverValue,
    bookValue,
    pageValue,
    coverRegenerationCount,
    bookRegenerationCount,
    pageRegenerationCount,
  ]);

  const hasChanges = Object.keys(changedFields).length > 0;

  const summaryText = useMemo((): string => {
    const parts: string[] = [];
    if (changedFields.coverRegenerationCount !== undefined) {
      parts.push(`cover=${String(changedFields.coverRegenerationCount)}`);
    }
    if (changedFields.bookRegenerationCount !== undefined) {
      parts.push(`book=${String(changedFields.bookRegenerationCount)}`);
    }
    if (changedFields.pageRegenerationCount !== undefined) {
      parts.push(`page=${String(changedFields.pageRegenerationCount)}`);
    }
    return `Set ${parts.join(', ')}?`;
  }, [changedFields]);

  const handleSave = useCallback((): void => {
    if (!hasChanges) return;
    modal.confirm({
      title: 'Update regeneration counts?',
      content: summaryText,
      okText: 'Save',
      cancelText: 'Cancel',
      onOk: async (): Promise<void> => {
        setSubmitting(true);
        try {
          await axiosInstance.patch(
            `/api/admin/orders/${orderId}/regeneration-counts`,
            changedFields
          );
          message.success('Regeneration counts updated');
          onSuccess();
        } catch (error) {
          const err = error as { message?: string };
          message.error(
            `Failed to update counts: ${err?.message ?? 'Unknown error'}`
          );
        } finally {
          setSubmitting(false);
        }
      },
    });
  }, [
    hasChanges,
    modal,
    summaryText,
    orderId,
    changedFields,
    message,
    onSuccess,
  ]);

  return (
    <Card
      title="Regeneration Attempts"
      size="small"
      style={{ marginTop: 16, marginBottom: 16 }}
    >
      <Form layout="vertical" disabled={submitting}>
        <Form.Item
          label="Cover"
          help={helperText(coverRegenerationCount, COVER_REGEN_LIMIT)}
        >
          <InputNumber
            min={0}
            value={coverValue}
            onChange={(v) => {
              if (typeof v === 'number') setCoverValue(v);
            }}
          />
        </Form.Item>
        <Form.Item
          label="Book"
          help={helperText(bookRegenerationCount, BOOK_REGEN_LIMIT)}
        >
          <InputNumber
            min={0}
            value={bookValue}
            onChange={(v) => {
              if (typeof v === 'number') setBookValue(v);
            }}
          />
        </Form.Item>
        <Form.Item
          label="Page"
          help={helperText(pageRegenerationCount, PAGE_REGEN_LIMIT)}
        >
          <InputNumber
            min={0}
            value={pageValue}
            onChange={(v) => {
              if (typeof v === 'number') setPageValue(v);
            }}
          />
        </Form.Item>
        <Button
          type="primary"
          disabled={!hasChanges || submitting}
          loading={submitting}
          onClick={handleSave}
        >
          Save
        </Button>
      </Form>
    </Card>
  );
}
